import Popup from './Popup.js';
import { getText } from '/app_core/src/translations/translations.js';
import { createIconMarkup } from '/app_core/src/utils/icon.js';

/**
 * Custom Alert Component
 * Replaces browser alerts with a custom-styled popup
 */
export default async function Alert({ title, message, type = 'info', onConfirm, onCancel, confirmText, cancelText }) {
    // Default button texts based on type
    const defaultTexts = {
        confirm: {
            info: 'ok',
            warning: 'ok',
            error: 'ok',
            success: 'ok',
            confirm: 'ok',
            delete: 'delete'
        },
        cancel: {
            info: null,
            warning: null,
            error: null,
            success: null,
            confirm: 'cancel',
            delete: 'cancel'
        }
    };

    // Use provided texts or defaults
    const finalConfirmText = confirmText || await getText(`alerts.buttons.${defaultTexts.confirm[type] || 'ok'}`);
    const finalCancelText = cancelText || (defaultTexts.cancel[type] ? await getText(`alerts.buttons.${defaultTexts.cancel[type]}`) : null);

    // Create the content with message only
    const content = document.createElement('div');
    content.className = 'alert-content';

    // Add message
    const messageEl = document.createElement('div');
    messageEl.className = 'alert-message';
    // Replace icon placeholders with actual SVG icons
    const iconRegex = /\{icon:([^}]+)\}/g;
    messageEl.innerHTML = message.replace(iconRegex, (match, iconName) => {
        return createIconMarkup(iconName, 'alert-inline-icon');
    });
    content.appendChild(messageEl);

    // Create button container for footer
    const buttonContainer = document.createElement('div');
    buttonContainer.className = 'alert-buttons';

    // Add cancel button if needed
    if (finalCancelText) {
        const cancelBtn = document.createElement('button');
        cancelBtn.className = 'alert-btn alert-btn-cancel';
        cancelBtn.textContent = finalCancelText;
        cancelBtn.onclick = () => {
            popup.close();
            if (typeof onCancel === 'function') onCancel();
        };
        buttonContainer.appendChild(cancelBtn);
    }

    // Add confirm button
    const confirmBtn = document.createElement('button');
    confirmBtn.className = `alert-btn alert-btn-confirm alert-btn-${type}`;
    confirmBtn.textContent = finalConfirmText;
    confirmBtn.onclick = () => {
        popup.close();
        if (typeof onConfirm === 'function') onConfirm();
    };
    buttonContainer.appendChild(confirmBtn);

    // Create and show popup with buttons in footer
    const popup = Popup({
        title: title,
        content: content,
        footer: buttonContainer,
        size: 'extra-small',
        onClose: () => {
            if (typeof onCancel === 'function') onCancel();
        }
    });

    // Focus the confirm button for better UX
    setTimeout(() => {
        confirmBtn.focus();
    }, 100);

    return popup;
}

/**
 * Confirmation Dialog
 * For yes/no or confirm/cancel scenarios
 */
export async function confirm({ title, message, onConfirm, onCancel, confirmText, cancelText }) {
    return await Alert({
        title,
        message,
        type: 'confirm',
        onConfirm,
        onCancel,
        confirmText,
        cancelText
    });
}

/**
 * Delete Confirmation Dialog
 * Specifically styled for delete operations
 */
export async function confirmDelete({ title, message, onConfirm, onCancel, confirmText, cancelText }) {
    return await Alert({
        title,
        message,
        type: 'delete',
        onConfirm,
        onCancel,
        confirmText,
        cancelText
    });
}

/**
 * Info Alert
 * For informational messages
 */
export async function info({ title, message, onConfirm, onCancel }) {
    return await Alert({
        title,
        message,
        type: 'info',
        onConfirm,
        onCancel
    });
}

/**
 * Success Alert
 * For success messages
 */
export async function success({ title, message, onConfirm }) {
    return await Alert({
        title,
        message,
        type: 'success',
        onConfirm
    });
}

/**
 * Warning Alert
 * For warning messages
 */
export async function warning({ title, message, onConfirm }) {
    return await Alert({
        title,
        message,
        type: 'warning',
        onConfirm
    });
}

/**
 * Error Alert
 * For error messages
 */
export async function error({ title, message, onConfirm }) {
    return await Alert({
        title,
        message,
        type: 'error',
        onConfirm
    });
} 